import { useCallback, useRef, useEffect } from 'react';
import { useDragDropContext } from '../contexts/DragDropContext';

export interface UseDragDropOptions {
  onDragStart?: (itemId: string, itemType: 'position' | 'sl' | 'tp', startPos: { x: number; y: number }) => void;
  onDragMove?: (currentPos: { x: number; y: number }) => void;
  onDragEnd?: (endPos: { x: number; y: number }, dropZone?: any) => void;
  onConfirm?: (action: any) => void;
  onCancel?: () => void;
}

export const useDragDrop = (options: UseDragDropOptions = {}) => {
  const {
    startDrag,
    updateDrag,
    endDrag,
    getDropZoneAt,
    setConfirming,
    setPendingAction,
    confirmAction,
    cancelAction,
    dragState,
    isConfirming,
    pendingAction,
  } = useDragDropContext();

  const dragRef = useRef<HTMLElement | null>(null);
  const isDraggingRef = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent, itemId: string, itemType: 'position' | 'sl' | 'tp', originalData: any) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startPos = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = true;
    
    startDrag(itemId, itemType, startPos, originalData);
    options.onDragStart?.(itemId, itemType, startPos);
  }, [startDrag, options]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current || !dragState.isDragging) return;

    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      // Only allow vertical movement - keep X position fixed
      // Constrain Y position to reasonable bounds (prevent dragging too far outside chart)
      const minY = 50; // Minimum Y position (top of chart area)
      const maxY = window.innerHeight - 50; // Maximum Y position (bottom of chart area)
      const constrainedY = Math.max(minY, Math.min(maxY, e.clientY));
      
      const currentPos = { 
        x: dragState.startPosition!.x, // Keep original X position
        y: constrainedY 
      };
      updateDrag(currentPos);
      options.onDragMove?.(currentPos);
    });
  }, [updateDrag, dragState.isDragging, options, dragState.startPosition]);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current || !dragState.isDragging) return;

    isDraggingRef.current = false;
    const endPos = { x: e.clientX, y: e.clientY };
    const dropZone = getDropZoneAt(endPos.x, endPos.y);
    
    // Set pending action but don't show popup - use existing buttons
    const action = {
      type: dragState.draggedItemType === 'position' ? 'move_position' : 
            dragState.draggedItemType === 'sl' ? 'update_sl' : 'update_tp',
      positionId: dragState.draggedItemId!,
      newPrice: dropZone?.price || null, // Use drop zone price if available
      newPosition: endPos,
      startPosition: dragState.startPosition, // Store start position for cancel
    };
    
    setPendingAction(action);
    setConfirming(true); // This will show the existing Cancel/Confirm buttons
    
    endDrag();
    options.onDragEnd?.(endPos, dropZone);
  }, [dragState, getDropZoneAt, endDrag, setConfirming, setPendingAction, options]);

  // Add global event listeners when dragging
  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState.isDragging, handleMouseMove, handleMouseUp]);

  const handleConfirm = useCallback(() => {
    if (pendingAction) {
      options.onConfirm?.(pendingAction);
      confirmAction();
    }
  }, [pendingAction, options, confirmAction]);

  const handleCancel = useCallback(() => {
    options.onCancel?.();
    cancelAction();
  }, [options, cancelAction]);

  return {
    dragRef,
    dragState,
    isConfirming,
    pendingAction,
    handleMouseDown,
    handleConfirm,
    handleCancel,
  };
};
